import * as DateF from 'date-fns'
import { useEffect, useState } from 'react'
import { Rifm } from 'rifm'
import styled from 'styled-components'

import { useHapticFeedback, AnimateHeight } from '@blade/ui-kit'

import { CalendarIcon } from '../../icons/icons'

import { DateInputCalendar } from './date-input-calendar'

import type { ClassNameProps } from '@root/shared/react'

export type DateInputProps = {
  value?: Date
  onAccept: (date: Date | undefined) => void
  errorMessage?: string
  placeholder?: string
  yearRange?: [number, number]
} & ClassNameProps

export const DateInput: React.FC<DateInputProps> = ({
  className,
  placeholder,
  onAccept,
  value,
  errorMessage,
  yearRange = [1900, 2200],
}) => {
  const haptics = useHapticFeedback()
  const [open, setOpen] = useState<boolean>(false)
  const [formattedValue, setFormattedValue] = useState<string>(
    value ? DateF.format(value, 'dd.MM.yyyy') : '',
  )

  useEffect(() => {
    setFormattedValue(value ? DateF.format(value, 'dd.MM.yyyy') : '')
  }, [value])

  const handleChange = (value: string) => {
    // Clamp days, months, and years in the input to valid maximums before continuing
    const clamped = clampDayMonthYear(value, yearRange)
    setFormattedValue(clamped)

    // reset value if input is empty
    if (clamped.length === 0) {
      onAccept(undefined)
      return
    }
    // accept only when value is 10 characters long
    if (isValidateDate(clamped, yearRange)) {
      const date = DateF.parse(clamped, 'dd.MM.yyyy', new Date())
      onAccept(date)
    }
  }
  const handleCalendarChange = (date: Date) => {
    setFormattedValue(DateF.format(date, 'dd.MM.yyyy'))
    onAccept(date)
  }
  const toggleCalendar = () => {
    haptics.impactOccurred('light')
    setOpen(!open)
  }

  return (
    <Root>
      <RootInput className={className}>
        <Rifm
          accept={/\d+/g}
          mask={10 <= formattedValue.length}
          format={formatDate}
          append={appendHyphen}
          value={formattedValue}
          onChange={handleChange}
        >
          {({ value, onChange }) => (
            <InputMasked
              type="tel"
              placeholder={placeholder}
              value={value}
              onChange={onChange}
            />
          )}
        </Rifm>
        <DateInputCalendar
          open={open}
          onOpenChange={toggleCalendar}
          onAccept={handleCalendarChange}
          value={
            formattedValue.length === 10
              ? DateF.parse(formattedValue, 'dd.MM.yyyy', new Date())
              : undefined
          }
        >
          <CalendarIconAlt size={12} />
        </DateInputCalendar>
      </RootInput>
      <AnimateHeight>
        {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : undefined}
      </AnimateHeight>
    </Root>
  )
}

const isValidateDate = (
  value: string,
  yearRange: [number, number],
): boolean => {
  if (value.length < 10) {
    return false
  }

  const clamped = clampDayMonthYear(value, yearRange)
  // Parse date strictly by parts to avoid 'new Date("31.13.2000")' becoming 'Invalid Date'
  const [dayStr, monthStr, yearStr] = clamped.split('.')
  const day = Number(dayStr)
  const month = Number(monthStr) - 1 // JS Date months: 0-11
  const year = Number(yearStr)
  const date = new Date(year, month, day)

  // Now check parts match, otherwise don't accept (e.g. 31.02.2000)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return false
  }

  return !isNaN(date.getTime())
}

const clampDayMonthYear = (
  value: string,
  yearRange: [number, number],
): string => {
  const maxDays = 31
  const maxMonths = 12
  const digits = (value.match(/\d+/g) || []).join('')
  const rawDay = digits.slice(0, 2)
  const rawMonth = digits.slice(2, 4)
  const rawYear = digits.slice(4, 8)

  const day =
    rawDay.length === 2
      ? (() => {
          const day = Number(rawDay)
          if (day > maxDays) {
            return maxDays.toString().padStart(2, '0')
          }
          if (day <= 0) {
            return '01'
          }
          return rawDay
        })()
      : rawDay

  const month =
    rawMonth.length === 2
      ? (() => {
          const month = Number(rawMonth)
          if (month > maxMonths) {
            return maxMonths.toString().padStart(2, '0')
          }
          if (month <= 0) {
            return '01'
          }
          return rawMonth
        })()
      : rawMonth

  const year =
    rawYear.length === 4
      ? (() => {
          let n = Number(rawYear)
          if (n < yearRange[0]) {
            n = yearRange[0]
          }
          if (n > yearRange[1]) {
            n = yearRange[1]
          }
          return n.toString().padStart(4, '0')
        })()
      : rawYear

  return [
    day,
    month.length > 0 ? month : undefined,
    year.length > 0 ? year : undefined,
  ]
    .filter((v) => v !== undefined)
    .join('.')
}

const formatDate = (value: string) => {
  const digits = (value.match(/\d+/g) || []).join('')
  const chars = [...digits]
  const output = chars
    .reduce(
      (r, v, index) => (index === 2 || index === 4 ? `${r}.${v}` : `${r}${v}`),
      '',
    )
    .slice(0, 10)

  if (value.endsWith('.')) {
    if (output.length === 2) {
      return `${output}.`
    }

    if (output.length === 5) {
      return `${output}.`
    }
  }

  return output
}

const appendHyphen = (value: string) =>
  value.length === 2 || value.length === 5 ? `${value}.` : value

const Root = styled.div``

const RootInput = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  border: 1px solid transparent;
  border-radius: 10px;
  background: var(--appearanceA-7);
  height: 40px;
  padding: 5px 12px 5px 15px;
  transition: all 300ms ease;

  align-items: center;

  &:focus-within {
    border-color: var(--backgroundA-5);
  }
`

const InputMasked = styled.input`
  background: transparent;
  color: var(--text-12);

  border: none;
  outline: none;
  height: 100%;
  width: 100%;

  font-size: 12px;
  font-weight: 600;
  line-height: 20px;

  &:disabled {
    color: var(--textA-8);
  }

  &::placeholder {
    color: var(--textA-8);
    font-weight: 400;
    line-height: 20px;
  }
`

const ErrorMessage = styled.div`
  color: var(--red-9);

  font-size: 10px;
  font-weight: 500;
  line-height: 16px;
`

const CalendarIconAlt = styled(CalendarIcon)`
  color: var(--text-8);

  transition: all 300ms ease;

  ${RootInput}:hover & {
    color: var(--textA-10);
  }
`
